import _extends from '@babel/runtime/helpers/esm/extends';
import _objectSpread from '@babel/runtime/helpers/esm/objectSpread2';
import _objectWithoutProperties from '@babel/runtime/helpers/esm/objectWithoutProperties';
import _toConsumableArray from '@babel/runtime/helpers/esm/toConsumableArray';
import _typeof from '@babel/runtime/helpers/esm/typeof';
import React, { useContext, createContext } from 'react';
import propTypes from 'prop-types';
import { useTheme, css, Flex, Box } from '@chakra-ui/react';
import mapValues from 'lodash.mapvalues';
import get from 'lodash.get';

var _excluded = ["children", "spacing", "spacingX", "spacingY", "minSize", "align", "wrap"],
  _excluded2 = ["width"];

/**
 * Usage:

import { Cluster, ClusterItem } from '.';

export default () => (
  <Cluster as='ul' spacing='1em' minSize='10em'>
    <li>1. Normal grid item</li>
    <ClusterItem as='li' width='100%'>2. Full width item</li>
    <li>3. Normal grid item</li>
    <li>4. Normal grid item</li>
    <ClusterItem as='li' width='20em'>5. Double width item</li>
    <li>4. Normal grid item</li>
  </Cluster>
)

 */

var SpacingContext = /*#__PURE__*/createContext();
var _mapRules = function mapRules(obj, mapper) {
  var path = arguments.length > 2 && arguments[2] !== undefined ? arguments[2] : [];
  return mapValues(obj, function (value, key) {
    if (value == null) {
      return value;
    }
    // For media queries we recurse
    if (_typeof(value) === 'object') {
      return _mapRules(value, mapper, [].concat(_toConsumableArray(path), [key]));
    }
    return mapper(value, obj, [].concat(_toConsumableArray(path), [key]));
  });
};

// See https://github.com/Heydon/fukol-grids
var Cluster = function Cluster(_ref) {
  var children = _ref.children,
    _ref$spacing = _ref.spacing,
    spacing = _ref$spacing === void 0 ? 4 : _ref$spacing,
    _ref$spacingX = _ref.spacingX,
    spacingX = _ref$spacingX === void 0 ? spacing : _ref$spacingX,
    _ref$spacingY = _ref.spacingY,
    spacingY = _ref$spacingY === void 0 ? spacing : _ref$spacingY,
    _ref$minSize = _ref.minSize,
    minSize = _ref$minSize === void 0 ? 'auto' : _ref$minSize,
    align = _ref.align,
    _ref$wrap = _ref.wrap,
    wrap = _ref$wrap === void 0 ? 'wrap' : _ref$wrap,
    props = _objectWithoutProperties(_ref, _excluded);
  var theme = useTheme();
  var margins = css({
    mx: spacingX,
    my: spacingY
  })(theme);
  var wrapperMargins = _mapRules(margins, function (margin) {
    return "calc(".concat(margin, " / -2)");
  });
  var childMargins = _mapRules(margins, function (margin) {
    return "calc(".concat(margin, " / 2)");
  });
  return /*#__PURE__*/React.createElement(SpacingContext.Provider, {
    value: {
      spacingX: spacingX,
      spacingY: spacingY
    }
  }, /*#__PURE__*/React.createElement(Flex, _extends({}, props, {
    wrap: wrap,
    align: align,
    sx: _objectSpread(_objectSpread({}, wrapperMargins), {}, {
      '& > *': _objectSpread(_objectSpread({}, childMargins), {}, {
        /* see https://github.com/Heydon/fukol-grids/issues/18 */
        minWidth: 0,
        minHeight: 0,
        flexGrow: 0,
        flexShrink: 0,
        flexBasis: minSize
      })
    })
  }), children));
};
Cluster.propTypes = {
  children: propTypes.node.isRequired,
  as: propTypes.node,
  /** Shorthand for specifying `spacingX` & `spacingY`. An array represents
   * responsive values */
  spacing: propTypes.oneOfType([propTypes.string, propTypes.number, propTypes.arrayOf(propTypes.oneOfType([propTypes.string, propTypes.number]))]),
  spacingX: propTypes.oneOfType([propTypes.string, propTypes.number, propTypes.arrayOf(propTypes.oneOfType([propTypes.string, propTypes.number]))]),
  spacingY: propTypes.oneOfType([propTypes.string, propTypes.number, propTypes.arrayOf(propTypes.oneOfType([propTypes.string, propTypes.number]))]),
  /** Uses the `space` scale */
  minSize: propTypes.oneOfType([propTypes.string, propTypes.number, propTypes.arrayOf(propTypes.oneOfType([propTypes.string, propTypes.number]))]),
  alignItems: propTypes.string,
  /** Alias for alignItems */
  align: propTypes.string,
  /** Specifies the `flex-wrap` CSS property */
  wrap: propTypes.string
};
var ClusterItem = function ClusterItem(_ref2) {
  var width = _ref2.width,
    props = _objectWithoutProperties(_ref2, _excluded2);
  var _useContext = useContext(SpacingContext),
    spacingX = _useContext.spacingX;
  var theme = useTheme();
  var rules = undefined;
  if (width) {
    // Normalise to dealing with arrays
    var spacingXResponsive = Array.isArray(spacingX) ? spacingX : [spacingX];
    var widthResponsive = Array.isArray(width) ? width : [width];

    // Ensure the `spacingX` matches the arity of the `width` prop by repeating the
    // last element as many times as is necessary
    if (spacingXResponsive.length < widthResponsive.length) {
      var extendedSpacingX = Array.from({
        length: widthResponsive.length - spacingXResponsive.length
      }, function () {
        return spacingXResponsive[spacingXResponsive.length - 1];
      });
      spacingXResponsive = [].concat(_toConsumableArray(spacingXResponsive), extendedSpacingX);
    } else if (spacingXResponsive.length > widthResponsive.length) {
      var extendedWidth = Array.from({
        length: spacingXResponsive.length - widthResponsive.length
      }, function () {
        return widthResponsive[widthResponsive.length - 1];
      });
      widthResponsive = [].concat(_toConsumableArray(widthResponsive), extendedWidth);
    }

    // For the next calculation, we have to ensure that every breakpoint
    // actually has a value, so we fill in any `null`s with their explicit value
    // of the base (ie; first element).
    // This is only necessary for spacingX since we will skip a breakpoint entirely
    // if it doesn't have a width set.
    spacingXResponsive = spacingXResponsive.map(function (value, index) {
      if (index === 0) {
        return value;
      }
      return value !== null && value !== void 0 ? value : spacingXResponsive[0];
    });

    // Convert the responsive values to CSS rules
    var marginRules = css({
      margin: spacingXResponsive
    })(theme);
    var flexBasisRules = css({
      flexBasis: widthResponsive
    })(theme);

    // Now we go over the rules, ensuring the flexBasis takes into account the
    // width if necessary
    // We also force `!important` due to specificity issues with setting the
    // flexBasis from the parent
    rules = _mapRules(flexBasisRules, function (rule, obj, path) {
      // a percentage width needs to be calculated.
      // see https://github.com/Heydon/fukol-grids#percentage-widths
      if (rule.indexOf('%') !== -1) {
        var spacing = get(marginRules, [].concat(_toConsumableArray(path.slice(0, -1)), ['margin']));
        return "calc(".concat(rule, " - ").concat(spacing, ") !important");
      }
      return "".concat(rule, " !important");
    });
  }
  return /*#__PURE__*/React.createElement(Box, _extends({}, props, {
    sx: rules
  }));
};
ClusterItem.propTypes = {
  children: propTypes.node.isRequired,
  as: propTypes.node,
  width: propTypes.string
};

export { Cluster, ClusterItem };
